import { Button, buttonVariants } from "@workspace/web-ui/components/button";
import { Image } from "@workspace/web-ui/components/image";
import { cn } from "@workspace/web-ui/lib/utils";
import { useState } from "react";
// import { MedicalRefillsComponent } from "~/features/dashboard/components/refills";
import { FormattedEncounter } from "../../../encounters/types";
import { Info, Pill } from "lucide-react";
import { MedicalRefillsSheet } from "~/features/dashboard/components/refills/refill-sheet";

interface InfoRowProps {
  icon: string;
  alt: string;
  value: string;
  testId?: string;
}

const MedicationRefillStatus = ({
  refillStatus = "Refill",
  actionStatus = "Action needed"
}: { refillStatus?: string; actionStatus?: string }) => {
  const isInProgress = actionStatus === "In progress";
  return (
    <div className="flex flex-row gap-2 items-center">
      {/* Green refill tag with pill icon */}
      <div
        className={cn(
          "flex flex-row gap-1 items-center rounded-full px-[6px] py-[4px] w-fit",
          "text-[#16A34A] bg-[#ECFDF3]"
        )}
        data-testid="refill-status"
      >
        <Pill className="w-3.5 h-3.5 text-[#16A34A]" />
        <span className="text-xs font-medium">{refillStatus}</span>
      </div>
      {/* Status tag - Orange for Action needed, Blue for In progress */}
      <div
        className={cn(
          "flex flex-row gap-1 items-center rounded-full px-[6px] py-[4px] w-fit",
          isInProgress
            ? "text-[#2563EB] bg-[#EFF6FF]"
            : "text-[#FFA726] bg-[#FFF5E6]"
        )}
        data-testid="medication-refill-status"
      >
        <Info className={cn("size-4", isInProgress ? "text-[#2563EB]" : "text-[#FFA726]")} />
        <span className="text-xs font-medium">{actionStatus}</span>
      </div>
    </div>
  );
};

const InfoRow = ({ icon, alt, value, testId }: InfoRowProps) => (
  <div className="flex gap-2 items-center" data-testid={testId}>
    <Image src={icon} alt={alt} className="w-4 h-4" layout="constrained" width={96} height={96} />
    <span className="text-sm font-normal text-[#202020CC]">{value}</span>
  </div>
);

interface ActionButtonsProps {
  onReviewMedication?: () => void;
}

const ActionButtons = ({ onReviewMedication }: ActionButtonsProps) => {
  return (
    <div className="flex gap-2 items-end" data-testid="action-buttons">
      <Button
        onClick={onReviewMedication}
        variant="default"
        className={cn(buttonVariants({ variant: "default" }), "rounded-[8px] !bg-[#4272DD] !text-white !hover:bg-[#3661C5] transition-colors button-primary !shadow-none !border-none h-fit")}
        aria-label="Review medication"
      >
        Review
      </Button>
    </div>
  );
};



type MedicationCardProps = {
  item: FormattedEncounter
  program: string
}

const MedicationCard = ({
  item,
  program
}: MedicationCardProps) => {
  const { id, title, date, medicalRefills } = item
  console.log("refill id", id)
  const [isReviewMedicationModalOpen, setIsReviewMedicationModalOpen] = useState(false);

  const meetingInfo = [
    { icon: "/icons/meeting-now.svg", alt: "refill date", value: date, testId: "refill-date" },
    ...(medicalRefills?.map((refill, index) => {
      const name = refill.name ? refill.name : "";
      const dosage = refill.dosage ? refill.dosage : "";
      const value =
        (!refill.name && !refill.dosage) ||
        (typeof refill.name === "undefined" && typeof refill.dosage === "undefined") ||
        (name.trim() === "" && dosage.trim() === "")
          ? "--"
          : `${name} ${dosage}`.trim();

      return {
        icon: "/icons/drugs.svg",
        alt: "refill drugs",
        value,
        testId: `refill-drugs-${index}`,
      };
    }) ?? [])
  ];

  const handleReviewMedication = () => {
    setIsReviewMedicationModalOpen(true);
  };

  // Calculate status for weight-loss program
  const getActionStatus = () => {
    if (program !== "weight-loss") {
      return "Action needed";
    }
    
    const tasks = item.recommendedTasks ?? [];
    const normalizedTasks = Array.isArray(tasks) ? tasks : [];
    
    const hasMedicationTask = normalizedTasks.some((task: any) => 
      task?.type === "medication_payment" && 
      task?.prescription?.medication && 
      Array.isArray(task.prescription.medication) && 
      task.prescription.medication.length > 0
    );
    // const hasTasks = normalizedTasks.length > 0;
    const isPaid = item.patient?.medical_plan?.payment_status === "paid";
    
    // If medication has been added (has medication_payment task with prescription)
    if (hasMedicationTask) {
      // If paid, show "Action needed", if not paid, show "In progress"
      return isPaid ? "Action needed" : "In progress";
    }
    
    // If there are tasks but no medication added yet, or no tasks at all
    return "Action needed";
  };

  return (
    <>
      <article
        className="flex md:flex-row flex-col gap-4 p-4 border border-[#E6E6E6] border-l-[6px] border-l-[#00C781] shadow-sm bg-white justify-between items-start w-full"
        data-testid="refill-card"
      >
        <div className="flex flex-col gap-3 w-full">
          <MedicationRefillStatus actionStatus={getActionStatus()} />
          <div className="flex flex-row gap-2 items-center">

            <h2
              className="text-xl font-semibold text-[#2D2F35]"
              data-testid="refill-title"
            >
              {title}
            </h2>
            {program === "urgent-care" ? (
              item.patient.urgent_care_onboarded && (
                <div className="">
                  <img src="/icons/check.svg" alt="timeline" />
                </div>
              )
            ) : (
              item.patient.medical_plan?.payment_status === "paid" && item.patient.glp1_guide_accepted && item.patient.identity_verified && item.patient.supplementary_health_assessment && Object.keys(item.patient.supplementary_health_assessment).length > 0 &&  (
                <div className="">
                  <img src="/icons/check.svg" alt="timeline" />
                </div>
              )
            )}
          </div>

          <div className="flex flex-col gap-2 items-start">
            {meetingInfo.map((info) => (
              <InfoRow
                key={info.testId}
                icon={info.icon}
                alt={info.alt}
                value={info.value ?? ""}
                testId={info.testId}
              />
            ))}
          </div>
        </div>

        <ActionButtons
          onReviewMedication={handleReviewMedication}
        />
      </article>

      {isReviewMedicationModalOpen && id && (
        <MedicalRefillsSheet
          refillId={id}
          userDosespotId={item?.patient?.user_dosespot_id ?? ""}
          sessionId={item?.id ?? ""}
          encounterId={item?.encounterId ?? ""}
          urgent_care_onboarded={item?.patient?.urgent_care_onboarded ?? false}
          recommendedTasks={item?.recommendedTasks ?? []}
          open={isReviewMedicationModalOpen}
          onOpenChange={setIsReviewMedicationModalOpen}
        />
      )}
    </>
  );
};

export default MedicationCard;